import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { NotFound } from '@client/components/NotFound';
import { useAreFeatureFlagsSettled, useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { selectUserOrgs, useAuthStore } from '@client/stores/AuthStore';
import { getAuth0Client, loginWithRedirect } from '@client/utils/auth/auth0Client';
import { redirectUriIsRegistered } from '@shared/utils/core/oauth-redirect-uri';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Alert, Form, Select } from 'antd';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

/**
 * /oauth/authorize — the user-facing OAuth consent page.
 *
 * Why a SPA route (not a server-rendered HTML page): the only place to check
 * whether the user is currently logged into Curium is the browser, because
 * the Auth0 SPA SDK stores tokens in localStorage (see
 * client/src/utils/auth/auth0Client.ts:13 — `cacheLocation: 'localstorage'`).
 *
 * Flow:
 *   1. Validate the OAuth query params client-side; render an inline error page
 *      if `redirect_uri` is malformed (never redirect to a URL we can't trust).
 *   2. Call `getAuth0Client().isAuthenticated()`. If false → loginWithRedirect
 *      with `appState.redirectTo = current URL` so /callback comes back here.
 *   3. Call `getTokenSilently()` to get a fresh Auth0 JWT (silent when the
 *      tenant session is still valid — no user-visible login screen).
 *   4. Call `GET /oauth/client-info?clientId=...` for sanitised metadata
 *      (clientName, validated redirectUris, same-origin logoUrl).
 *   5. Render the consent form: org picker + expiry + token name + exact
 *      redirect_uri display + Authorise/Deny.
 *   6. On Authorise: `POST /oauth/consent` with the JWT → server returns
 *      `{ redirectTo }` → window.location.assign(redirectTo) bounces the
 *      browser to the MCP client's loopback callback with the auth code.
 *   7. On Deny: window.location.assign(`<redirect_uri>?error=access_denied&state=...`).
 *
 * The whole flow gates on the `enable-mcp-server` feature flag — when it's off
 * we render a 404-ish "not available" state so we don't leak the endpoint's
 * existence in screenshots or status checks.
 */

// A PKCE S256 `code_challenge` is BASE64URL(SHA-256(verifier)) — strictly the base64url
// alphabet (`A–Z a–z 0–9 - _`), no padding. The unreserved verifier characters `.` and `~`
// (RFC 7636 §4.1) are NOT valid in an S256 challenge, so they are excluded here to keep
// client validation aligned with what the server accepts.
const CHALLENGE_OK = /^[A-Za-z0-9_-]{43,128}$/;

const searchSchema = z.object({
    response_type: z.literal('code'),
    client_id: z.url(),
    redirect_uri: z.string().min(1),
    code_challenge: z.string().refine((v) => CHALLENGE_OK.test(v), {
        message: 'code_challenge must be 43–128 base64url chars',
    }),
    code_challenge_method: z.literal('S256'),
    state: z.string().min(1),
    scope: z.literal('curium:full'),
    resource: z.url(),
});

type SearchParams = z.infer<typeof searchSchema>;

const EXPIRY_OPTIONS = [
    { value: 30, label: '30 days' },
    { value: 60, label: '60 days' },
    { value: 90, label: '90 days (recommended)' },
] as const;

const CURIUM_LOGO_URL = 'https://curium-public.syd1.digitaloceanspaces.com/brand/curium-logo.webp';

export const Route = createFileRoute('/oauth/authorize')({
    validateSearch: zodValidator(searchSchema),
    component: OAuthAuthorize,
});

type ClientInfo = {
    clientId: string;
    clientName: string;
    redirectUris: string[];
    logoUrl: string | null;
};

type FetchState =
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ready'; info: ClientInfo }
    | { kind: 'error'; message: string };

function OAuthAuthorize() {
    const search = Route.useSearch() as SearchParams;
    const featureEnabled = useFeatureFlag('enable-mcp-server', false);
    // `useFeatureFlag` reads `false` for "not evaluated yet" as well as "off", and this route
    // sits outside `RequireAuth` / `RouteAccessGuard`, so nothing upstream waits for PostHog.
    // Its normal caller is an MCP client opening a fresh browser tab (and then the return leg
    // of the Auth0 redirect) — cold loads where `enable-mcp-server` is unknown until `/flags`
    // answers, because the server bootstrap only carries the maintenance flags. Treat the flag
    // as unknown until initialisation has settled rather than 404ing on the transient value.
    // No identity is passed: this page never runs `identifyUser()`, so init-only readiness is
    // the definitive signal here.
    const areFeatureFlagsSettled = useAreFeatureFlagsSettled();

    // Validate the redirect_uri locally first. Any redirect-bearing failure thereafter
    // MUST go back to redirect_uri (after the server re-validates), but an unparseable
    // redirect_uri can never be trusted, so we render inline.
    const redirectUriParsed = useMemo(() => {
        try {
            return new URL(search.redirect_uri);
        } catch {
            return null;
        }
    }, [search.redirect_uri]);

    if (!areFeatureFlagsSettled) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Loading size="large" />
            </div>
        );
    }

    if (!featureEnabled) {
        // Render the 404 in place rather than navigating away: a navigation would be
        // irreversible, and it would also drop the OAuth query string the MCP client needs us
        // to carry through the consent flow.
        return <NotFound />;
    }

    if (!redirectUriParsed) {
        return <InlineError title="Invalid redirect URI" message="The redirect_uri parameter is not a valid URL." />;
    }

    return <ConsentFlow search={search} redirectUri={redirectUriParsed} />;
}

function ConsentFlow({ search, redirectUri }: { search: SearchParams; redirectUri: URL }) {
    const userOrgs = useAuthStore(selectUserOrgs);
    const [authChecked, setAuthChecked] = useState(false);
    const [clientInfo, setClientInfo] = useState<FetchState>({ kind: 'idle' });
    const [jwt, setJwt] = useState<string | null>(null);

    // Step 1+2: Auth0 session check, then fresh JWT.
    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const client = getAuth0Client();
                const authed = await client.isAuthenticated();
                if (!authed) {
                    await loginWithRedirect({
                        appState: { redirectTo: `${window.location.pathname}${window.location.search}` },
                    });
                    return; // browser navigates away
                }
                const token = await client.getTokenSilently();
                if (cancelled) return;
                setJwt(token);
                setAuthChecked(true);
            } catch {
                if (cancelled) return;
                // Silent refresh failed — push the user through the explicit login flow.
                await loginWithRedirect({
                    appState: { redirectTo: `${window.location.pathname}${window.location.search}` },
                });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Step 3: fetch client metadata.
    useEffect(() => {
        if (!authChecked) return;
        let cancelled = false;
        setClientInfo({ kind: 'loading' });
        void (async () => {
            try {
                const url = new URL('/oauth/client-info', window.location.origin);
                url.searchParams.set('clientId', search.client_id);
                const res = await fetch(url.toString(), { credentials: 'omit' });
                if (!res.ok) {
                    const body = (await res.json().catch(() => ({}))) as { error_description?: string; error?: string };
                    const detail = body.error_description ?? body.error ?? `HTTP ${res.status}`;
                    if (!cancelled) setClientInfo({ kind: 'error', message: detail });
                    return;
                }
                const info = (await res.json()) as ClientInfo;
                // Port-flexible match for loopback redirect URIs (RFC 8252 §7.3) — native
                // clients register a portless callback and bind an ephemeral port at runtime.
                if (!redirectUriIsRegistered(search.redirect_uri, info.redirectUris)) {
                    if (!cancelled) {
                        setClientInfo({
                            kind: 'error',
                            message: 'The requested redirect URI is not registered for this client.',
                        });
                    }
                    return;
                }
                if (!cancelled) setClientInfo({ kind: 'ready', info });
            } catch (err) {
                if (!cancelled) {
                    setClientInfo({
                        kind: 'error',
                        message: err instanceof Error ? err.message : 'Failed to fetch client info',
                    });
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [authChecked, search.client_id, search.redirect_uri]);

    if (!authChecked || clientInfo.kind === 'idle' || clientInfo.kind === 'loading') {
        return (
            <PageController route={Route}>
                <ConsentChrome>
                    <div className="flex flex-col items-center gap-3 py-10">
                        <Loading size="large" />
                        <p className="text-muted-foreground">Loading…</p>
                    </div>
                </ConsentChrome>
            </PageController>
        );
    }

    if (clientInfo.kind === 'error') {
        // Errors after redirect_uri has been validated MAY redirect back to the client per
        // RFC 6749 §4.1.2.1, but we've also failed our local trust checks at this point,
        // so prefer to render an inline error rather than redirect.
        return (
            <InlineError
                title="Cannot authorise this app"
                message={clientInfo.message}
                code={`client_id: ${search.client_id}`}
            />
        );
    }

    return (
        <PageController route={Route}>
            <ConsentChrome>
                <ConsentForm
                    search={search}
                    redirectUri={redirectUri}
                    clientInfo={clientInfo.info}
                    jwt={jwt!}
                    userOrgs={userOrgs}
                />
            </ConsentChrome>
        </PageController>
    );
}

function ConsentChrome({ children }: { children: React.ReactNode }) {
    return (
        <div className="min-h-screen w-full bg-muted">
            <div className="flex min-h-screen items-center justify-center px-4">
                <div className="w-full max-w-lg">
                    <div className="mb-6 flex justify-center">
                        <img src={CURIUM_LOGO_URL} alt="Curium" className="h-12 w-auto" />
                    </div>
                    <div className="rounded-lg border border-separator bg-card p-6 sm:p-8">{children}</div>
                </div>
            </div>
        </div>
    );
}

type ConsentFormValues = {
    orgId: string;
    expiryDays: number;
    tokenName: string;
};

function ConsentForm({
    search,
    redirectUri,
    clientInfo,
    jwt,
    userOrgs,
}: {
    search: SearchParams;
    redirectUri: URL;
    clientInfo: ClientInfo;
    jwt: string;
    userOrgs: ReturnType<typeof selectUserOrgs>;
}) {
    const [form] = Form.useForm<ConsentFormValues>();
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const isLoopback = useMemo(() => {
        const host = redirectUri.hostname.toLowerCase();
        return host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host === '::1';
    }, [redirectUri]);

    // Subtitle under the client name — the trust anchor for the app's identity. A CIMD client_id
    // is a third-party metadata URL, so its host (e.g. "claude.ai") is a domain the client proved
    // it controls. A dynamically-registered (RFC 7591) client_id is minted on our own origin and
    // carries no domain-ownership proof, so showing our host would mislead and the self-declared
    // client_name can't be trusted either. Anchor on the one verifiable fact instead — the
    // callback host the token will be delivered to (also shown in full below).
    const clientSubtitle = useMemo(() => {
        try {
            const clientUrl = new URL(search.client_id);
            if (clientUrl.origin === window.location.origin) return `Redirects to ${redirectUri.host}`;
            return clientUrl.host;
        } catch {
            return search.client_id;
        }
    }, [search.client_id, redirectUri]);

    const handleAuthorise = async (values: ConsentFormValues): Promise<void> => {
        setSubmitting(true);
        setSubmitError(null);
        try {
            const res = await fetch('/oauth/consent', {
                method: 'POST',
                credentials: 'omit',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${jwt}`,
                },
                body: JSON.stringify({
                    clientId: search.client_id,
                    redirectUri: search.redirect_uri,
                    state: search.state,
                    codeChallenge: search.code_challenge,
                    codeChallengeMethod: search.code_challenge_method,
                    scope: search.scope,
                    resource: search.resource,
                    orgId: values.orgId,
                    expiryDays: values.expiryDays,
                    tokenName: values.tokenName.trim(),
                }),
            });
            if (!res.ok) {
                const body = (await res.json().catch(() => ({}))) as { error_description?: string; error?: string };
                setSubmitError(body.error_description ?? body.error ?? `Authorisation failed (HTTP ${res.status})`);
                setSubmitting(false);
                return;
            }
            const data = (await res.json()) as { redirectTo: string };
            // Server validated the redirect URL; client just navigates.
            window.location.assign(data.redirectTo);
        } catch (err) {
            setSubmitError(err instanceof Error ? err.message : 'Network error');
            setSubmitting(false);
        }
    };

    const handleDeny = (): void => {
        const denyUrl = new URL(redirectUri.toString());
        denyUrl.searchParams.set('error', 'access_denied');
        denyUrl.searchParams.set('state', search.state);
        window.location.assign(denyUrl.toString());
    };

    return (
        <div>
            <div className="mb-6 flex items-start gap-4">
                {clientInfo.logoUrl ? (
                    <img
                        src={clientInfo.logoUrl}
                        alt={clientInfo.clientName}
                        className="h-12 w-12 rounded border border-separator object-contain"
                    />
                ) : (
                    <div className="flex h-12 w-12 items-center justify-center rounded border border-separator bg-muted">
                        <ShieldCheck className="h-6 w-6 text-muted-foreground" />
                    </div>
                )}
                <div className="flex-1">
                    <h1 className="font-bold text-foreground text-xl">{clientInfo.clientName}</h1>
                    <p className="break-all text-muted-foreground text-sm">{clientSubtitle}</p>
                </div>
            </div>

            <p className="mb-4 text-foreground">
                <span className="font-semibold">{clientInfo.clientName}</span> is requesting access to your Curium
                account. This will let it read and modify data in the selected organisation, acting as you, until you
                revoke it.
            </p>

            <div className="mb-4 rounded-lg border border-separator bg-muted p-3 text-sm">
                <div className="mb-1 font-semibold text-muted-foreground text-xs uppercase">Will redirect to</div>
                <code className="block break-all font-mono text-foreground">{redirectUri.toString()}</code>
                {isLoopback && (
                    <p className="mt-2 text-muted-foreground text-xs">
                        This is a local loopback address used by desktop apps. Only continue if you started this
                        authorisation yourself.
                    </p>
                )}
            </div>

            <Form<ConsentFormValues>
                form={form}
                layout="vertical"
                onFinish={handleAuthorise}
                initialValues={{
                    orgId: userOrgs[0]?.id.toString() ?? '',
                    expiryDays: 90,
                    tokenName: clientInfo.clientName,
                }}
            >
                <Form.Item<ConsentFormValues>
                    name="orgId"
                    label="Organisation"
                    rules={[{ required: true, message: 'Choose the organisation to authorise access for' }]}
                >
                    <Select
                        options={userOrgs.map((o) => ({ value: o.id.toString(), label: o.name }))}
                        disabled={userOrgs.length <= 1}
                    />
                </Form.Item>

                <Form.Item<ConsentFormValues> name="expiryDays" label="Token expires in" rules={[{ required: true }]}>
                    <Select options={EXPIRY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))} />
                </Form.Item>

                <Form.Item<ConsentFormValues>
                    name="tokenName"
                    label="Token name"
                    rules={[
                        { required: true, whitespace: true, message: 'Give this authorisation a memorable name' },
                        { max: 100, message: 'Token name must be 100 characters or fewer' },
                    ]}
                    extra="Shown on the Authorised apps page so you can identify and revoke this token later."
                >
                    <input
                        className="w-full rounded border border-separator bg-card px-3 py-2 text-foreground"
                        maxLength={100}
                    />
                </Form.Item>

                {submitError && (
                    <Alert
                        type="error"
                        showIcon
                        icon={<AlertTriangle className="h-4 w-4" />}
                        message={submitError}
                        className="mb-4"
                    />
                )}

                <div className="flex gap-3">
                    <Button block size="large" onClick={handleDeny} disabled={submitting}>
                        Deny
                    </Button>
                    <Button block size="large" type="primary" htmlType="submit" loading={submitting}>
                        Authorise
                    </Button>
                </div>
            </Form>
        </div>
    );
}

function InlineError({ title, message, code }: { title: string; message: string; code?: string }) {
    return (
        <PageController route={Route}>
            <ConsentChrome>
                <div className="text-center">
                    <div className="mb-4 flex justify-center">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                            <AlertTriangle className="h-8 w-8 text-error" />
                        </div>
                    </div>
                    <h1 className="mb-2 font-bold text-foreground text-xl">{title}</h1>
                    <p className="mb-4 text-muted-foreground">{message}</p>
                    {code && (
                        <code className="block break-all rounded bg-muted px-2 py-1 text-muted-foreground text-xs">
                            {code}
                        </code>
                    )}
                </div>
            </ConsentChrome>
        </PageController>
    );
}
